import { useState, useRef, useEffect } from 'react';
import ScrollReveal from '../ScrollReveal';
import { t } from '@/data/translations';
import type { Lang } from '@/pages/Index';

type FontOption = 'inter' | 'playfair' | 'mono';
type ColorOption = 'silver' | 'ocean' | 'forest' | 'gold';
type GradientOption = 'subtle' | 'vivid' | 'minimal';
type DensityOption = 'compact' | 'normal' | 'airy';

interface CustomizationPanelProps {
  onFontChange: (font: FontOption) => void;
  onColorChange: (color: ColorOption) => void;
  onGradientChange: (gradient: GradientOption) => void;
  onDensityChange: (density: DensityOption) => void;
  currentFont: FontOption;
  currentColor: ColorOption;
  currentGradient: GradientOption;
  currentDensity: DensityOption;
  lang: Lang;
}

const CustomizationPanel = ({
  onFontChange,
  onColorChange,
  onGradientChange,
  onDensityChange,
  currentFont,
  currentColor,
  currentGradient,
  currentDensity,
  lang,
}: CustomizationPanelProps) => {
  const tr = t(lang).customization;
  const [expandedCard, setExpandedCard] = useState<string | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(event.target as Node)) {
        setExpandedCard(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fonts: { value: FontOption; label: string; preview: string }[] = [
    { value: 'inter', label: tr.fontSans, preview: 'Aa' },
    { value: 'playfair', label: tr.fontSerif, preview: 'Aa' },
    { value: 'mono', label: tr.fontMono, preview: 'Aa' },
  ];

  const colors: { value: ColorOption; label: string; hue: string }[] = [
    { value: 'silver', label: tr.colorSilver, hue: 'bg-[hsl(220,10%,50%)]' },
    { value: 'ocean', label: tr.colorOcean, hue: 'bg-[hsl(200,60%,40%)]' },
    { value: 'forest', label: tr.colorForest, hue: 'bg-[hsl(150,40%,35%)]' },
    { value: 'gold', label: tr.colorGold, hue: 'bg-[hsl(38,60%,50%)]' },
  ];

  const gradients: { value: GradientOption; label: string; desc: string }[] = [
    { value: 'subtle', label: tr.gradientSubtle, desc: tr.gradientSubtleDesc },
    { value: 'vivid', label: tr.gradientVivid, desc: tr.gradientVividDesc },
    { value: 'minimal', label: tr.gradientMinimal, desc: tr.gradientMinimalDesc },
  ];

  const densities: { value: DensityOption; label: string; icon: string }[] = [
    { value: 'compact', label: tr.densityCompact, icon: '▪▪▪' },
    { value: 'normal', label: tr.densityNormal, icon: '■ ■ ■' },
    { value: 'airy', label: tr.densityAiry, icon: '□   □   □' },
  ];

  const toggleCard = (cardId: string) => {
    setExpandedCard(expandedCard === cardId ? null : cardId);
  };

  return (
    <ScrollReveal delay={400}>
      <div ref={panelRef} className="mt-20 sm:mt-24 md:mt-16 grid grid-cols-2 gap-4 sm:grid-cols-4 sm:gap-6 max-w-4xl mx-auto w-full">
        {/* Typography Card */}
        <div 
          className={`group relative bg-card/60 backdrop-blur-sm border border-border/50 rounded-lg p-3 sm:p-4 cursor-pointer transition-all duration-300 hover:border-primary/30 hover:shadow-lg ${expandedCard === 'font' ? 'ring-1 ring-primary/20' : ''}`}
          onClick={() => toggleCard('font')}
        >
          <div className="flex flex-col items-center text-center">
            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-muted flex items-center justify-center mb-2 sm:mb-3">
              <span className={`text-base sm:text-lg font-medium text-foreground ${currentFont === 'playfair' ? 'font-serif' : currentFont === 'mono' ? 'font-mono' : ''}`}>
                Aa
              </span>
            </div>
            <h4 className="text-xs sm:text-sm font-medium text-foreground mb-0.5 sm:mb-1">{tr.typography}</h4>
            <p className="text-[10px] sm:text-xs text-muted-foreground capitalize">{currentFont === 'inter' ? 'Sans' : currentFont === 'playfair' ? 'Serif' : 'Mono'}</p>
          </div>
          
          {expandedCard === 'font' && (
            <div className="absolute bottom-full left-0 right-0 mb-2 bg-card border border-border rounded-lg p-2 sm:p-3 shadow-xl z-20">
              <div className="flex flex-col gap-1 sm:gap-2">
                {fonts.map((font) => (
                  <button
                    key={font.value}
                    onClick={(e) => { e.stopPropagation(); onFontChange(font.value); }}
                    className={`flex items-center justify-between px-2 sm:px-3 py-1.5 sm:py-2 rounded-md transition-colors ${currentFont === font.value ? 'bg-primary/10 text-primary' : 'hover:bg-muted'}`}
                  >
                    <span className={`text-xs sm:text-sm ${font.value === 'playfair' ? 'font-serif' : font.value === 'mono' ? 'font-mono' : ''}`}>
                      {font.label}
                    </span>
                    <span className={`text-base sm:text-lg ${font.value === 'playfair' ? 'font-serif' : font.value === 'mono' ? 'font-mono' : ''}`}>
                      {font.preview}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Color Card */}
        <div 
          className={`group relative bg-card/60 backdrop-blur-sm border border-border/50 rounded-lg p-3 sm:p-4 cursor-pointer transition-all duration-300 hover:border-primary/30 hover:shadow-lg ${expandedCard === 'color' ? 'ring-1 ring-primary/20' : ''}`}
          onClick={() => toggleCard('color')}
        >
          <div className="flex flex-col items-center text-center">
            <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full mb-2 sm:mb-3 ${colors.find(c => c.value === currentColor)?.hue}`} />
            <h4 className="text-xs sm:text-sm font-medium text-foreground mb-0.5 sm:mb-1">{tr.color}</h4>
            <p className="text-[10px] sm:text-xs text-muted-foreground">{colors.find(c => c.value === currentColor)?.label}</p>
          </div>
          
          {expandedCard === 'color' && (
            <div className="absolute bottom-full left-0 right-0 mb-2 bg-card border border-border rounded-lg p-2 sm:p-3 shadow-xl z-20">
              <div className="flex flex-col gap-1 lg:grid lg:grid-cols-2 lg:gap-2">
                {colors.map((color) => (
                  <button
                    key={color.value}
                    onClick={(e) => { e.stopPropagation(); onColorChange(color.value); }}
                    className={`flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 py-1.5 sm:py-2 rounded-md transition-colors ${currentColor === color.value ? 'bg-primary/10' : 'hover:bg-muted'}`}
                  >
                    <span className={`w-3 h-3 sm:w-4 sm:h-4 rounded-full flex-shrink-0 ${color.hue}`} />
                    <span className="text-[10px] sm:text-xs">{color.label}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Gradient Card */}
        <div 
          className={`group relative bg-card/60 backdrop-blur-sm border border-border/50 rounded-lg p-3 sm:p-4 cursor-pointer transition-all duration-300 hover:border-primary/30 hover:shadow-lg ${expandedCard === 'gradient' ? 'ring-1 ring-primary/20' : ''}`}
          onClick={() => toggleCard('gradient')}
        >
          <div className="flex flex-col items-center text-center">
            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-gradient-to-br from-primary/20 to-primary/5 mb-2 sm:mb-3 flex items-center justify-center">
              <svg className="w-4 h-4 sm:w-5 sm:h-5 text-primary/60" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <h4 className="text-xs sm:text-sm font-medium text-foreground mb-0.5 sm:mb-1">{tr.gradient}</h4>
            <p className="text-[10px] sm:text-xs text-muted-foreground">{gradients.find(g => g.value === currentGradient)?.label}</p>
          </div>
          
          {expandedCard === 'gradient' && (
            <div className="absolute bottom-full left-0 right-0 mb-2 bg-card border border-border rounded-lg p-2 sm:p-3 shadow-xl z-20">
              <div className="flex flex-col gap-1 sm:gap-2">
                {gradients.map((gradient) => (
                  <button
                    key={gradient.value}
                    onClick={(e) => { e.stopPropagation(); onGradientChange(gradient.value); }}
                    className={`flex flex-col items-start px-2 sm:px-3 py-1.5 sm:py-2 rounded-md transition-colors ${currentGradient === gradient.value ? 'bg-primary/10 text-primary' : 'hover:bg-muted'}`}
                  >
                    <span className="text-xs sm:text-sm font-medium">{gradient.label}</span>
                    <span className="text-[10px] sm:text-xs text-muted-foreground">{gradient.desc}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Density Card */}
        <div 
          className={`group relative bg-card/60 backdrop-blur-sm border border-border/50 rounded-lg p-3 sm:p-4 cursor-pointer transition-all duration-300 hover:border-primary/30 hover:shadow-lg ${expandedCard === 'density' ? 'ring-1 ring-primary/20' : ''}`}
          onClick={() => toggleCard('density')}
        >
          <div className="flex flex-col items-center text-center">
            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-muted flex items-center justify-center mb-2 sm:mb-3">
              <span className="text-[8px] sm:text-xs text-muted-foreground tracking-widest">
                {densities.find(d => d.value === currentDensity)?.icon}
              </span>
            </div>
            <h4 className="text-xs sm:text-sm font-medium text-foreground mb-0.5 sm:mb-1">{tr.density}</h4>
            <p className="text-[10px] sm:text-xs text-muted-foreground">{densities.find(d => d.value === currentDensity)?.label}</p>
          </div>
          
          {expandedCard === 'density' && (
            <div className="absolute bottom-full left-0 right-0 mb-2 bg-card border border-border rounded-lg p-2 sm:p-3 shadow-xl z-20">
              <div className="flex flex-col gap-1 sm:gap-2">
                {densities.map((density) => (
                  <button
                    key={density.value}
                    onClick={(e) => { e.stopPropagation(); onDensityChange(density.value); }}
                    className={`flex items-center justify-between px-2 sm:px-3 py-1.5 sm:py-2 rounded-md transition-colors ${currentDensity === density.value ? 'bg-primary/10 text-primary' : 'hover:bg-muted'}`}
                  >
                    <span className="text-xs sm:text-sm">{density.label}</span>
                    <span className="text-[8px] sm:text-xs tracking-widest">{density.icon}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </ScrollReveal>
  );
};

export default CustomizationPanel;

export type { FontOption, ColorOption, GradientOption, DensityOption };
